import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { useReminder, useCreateReminder, useUpdateReminder } from '../../../hooks/useApi';
import UserMultiPicker from './components/UserMultiPicker';
import { 
    IconArrowLeft, IconSave, IconPlus, IconTrash, IconX,
    IconCalendar, IconClock, IconRepeat, IconUsers, IconTag
} from '../../components/icons';
import {
    RECURRENCE_TYPES,
    DEFAULT_WEEKDAYS,
    DAYS_OF_WEEK,
    calculateFutureOccurrences,
    formatOccurrence
} from '../../../../lib/shared/schedule-calculator.js';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const COLOR_OPTIONS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
const ICON_OPTIONS = ['🔔', '📅', '⏰', '📌', '💼', '🎯', '📝', '✅', '⚡', '🚀', '💡', '📊'];

const ReminderEditor = () => {
    const { t } = useI18n(['common', 'reminders']);
    const { user } = useAuth();
    const { reminderId } = useParams();
    const navigate = useNavigate();
    const isEdit = !!reminderId;

    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('basic'); // basic, schedule, collaborators, advanced

    // React Query hooks
    const { data: reminderResult, isLoading: loading } = useReminder(reminderId);
    const createMutation = useCreateReminder();
    const updateMutation = useUpdateReminder();
    const saving = createMutation.isPending || updateMutation.isPending;

    // Form state - 預設使用weekly排程，寄送日為工作日
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        color: '#3B82F6',
        icon: '🔔',
        tags: [],
        schedule: {
            startDate: new Date().toISOString().slice(0, 16),
            endDate: '',
            time: '09:00',
            timezone: 'Asia/Shanghai',
            recurrence: {
                type: 'weekly', // 預設weekly排程
                interval: 1,
                daysOfWeek: DEFAULT_WEEKDAYS, // 預設工作日 (一到五)
                daysOfMonth: [1] // 支援多個日期
            }
        },
        collaborators: [],
        notifications: {
            enabled: true,
            channels: ['email'],
            notifyCollaborators: true
        },
        customFields: [],
        status: 'active'
    });
    console.log('formData:', formData);
    const [newTag, setNewTag] = useState('');
    const [showUserPicker, setShowUserPicker] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);
    const [previewCount, setPreviewCount] = useState(5); // 新增：預覽數量

    // Populate form with fetched data
    useEffect(() => {
        if (isEdit && reminderResult?.data && !formInitialized) {
            const reminder = reminderResult.data;
            
            // Check permission
            if (reminder.myPermission === 'read') {
                setError(t('readOnlyAccess', { ns: 'reminders' }));
                return;
            }

            // Format dates for input
            setFormData({
                ...reminder,
                schedule: {
                    ...reminder.schedule,
                    startDate: reminder.schedule?.startDate?.slice(0, 16) || '',
                    endDate: reminder.schedule?.endDate?.slice(0, 16) || '',
                    timezone: reminder.schedule?.timezone || 'Asia/Shanghai'
                }
            });
            setFormInitialized(true);
        }
    }, [reminderResult, isEdit, formInitialized, t]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleScheduleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, [field]: value }
        }));
    };

    const handleRecurrenceChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                recurrence: { ...prev.schedule.recurrence, [field]: value }
            }
        }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tag) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t !== tag)
        }));
    };

    const handleAddCollaborators = (selectedUsers) => {
        const existingIds = formData.collaborators.map(c => c.userId);
        const newCollaborators = selectedUsers
            .filter(u => !existingIds.includes(u.id))
            .map(u => ({
                userId: u.id,
                userName: u.displayName,
                userEmail: u.mail,
                permission: 'read'
            }));
        
        if (newCollaborators.length > 0) {
            setFormData(prev => ({
                ...prev,
                collaborators: [...prev.collaborators, ...newCollaborators]
            }));
        }
        setShowUserPicker(false);
    };

    const handleUpdateCollaboratorPermission = (userId, permission) => {
        setFormData(prev => ({
            ...prev,
            collaborators: prev.collaborators.map(c =>
                c.userId === userId ? { ...c, permission } : c
            )
        }));
    };

    const handleRemoveCollaborator = (userId) => {
        setFormData(prev => ({
            ...prev,
            collaborators: prev.collaborators.filter(c => c.userId !== userId)
        }));
    };

    const handleDayOfWeekToggle = (day) => {
        const current = formData.schedule.recurrence.daysOfWeek || [];
        const newDays = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day].sort((a, b) => a - b);
        handleRecurrenceChange('daysOfWeek', newDays);
    };

    const handleDayOfMonthToggle = (day) => {
        const current = formData.schedule.recurrence.daysOfMonth || [];
        const newDays = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day].sort((a, b) => a - b);
        handleRecurrenceChange('daysOfMonth', newDays);
    };

    // 計算未來排程預覽
    const futureOccurrences = useMemo(() => {
        if (!formData.schedule.startDate) return [];
        try {
            return calculateFutureOccurrences(formData.schedule, previewCount);
        } catch {
            return [];
        }
    }, [
        formData.schedule.startDate,
        formData.schedule.endDate,
        formData.schedule.time,
        formData.schedule.timezone,
        formData.schedule.recurrence.type,
        formData.schedule.recurrence.interval,
        JSON.stringify(formData.schedule.recurrence.daysOfWeek || []),
        JSON.stringify(formData.schedule.recurrence.daysOfMonth || []),
        previewCount
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            // Prepare data
            const submitData = {
                ...formData,
                schedule: {
                    ...formData.schedule,
                    startDate: new Date(formData.schedule.startDate).toISOString(),
                    endDate: formData.schedule.endDate 
                        ? new Date(formData.schedule.endDate).toISOString() 
                        : null
                }
            };

            if (isEdit) {
                await updateMutation.mutateAsync({ reminderId, data: submitData });
            } else {
                await createMutation.mutateAsync(submitData);
            }
            
            navigate('/reminders');
        } catch (err) {
            setError(err.message || t('error', { ns: 'common' }));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const tabs = [
        { id: 'basic', label: t('tabs.basic', { ns: 'reminders' }) },
        { id: 'schedule', label: t('tabs.schedule', { ns: 'reminders' }) },
        { id: 'collaborators', label: t('tabs.collaborators', { ns: 'reminders' }) },
        { id: 'advanced', label: t('tabs.advanced', { ns: 'reminders' }) }
    ];

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <Link
                        to="/reminders"
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    >
                        <IconArrowLeft className="w-5 h-5" />
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEdit ? t('editReminder', { ns: 'reminders' }) : t('newReminder', { ns: 'reminders' })}
                    </h1>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="flex gap-4 -mb-px">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Basic Tab */}
                {activeTab === 'basic' && (
                    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                        {/* Icon & Color Selection */}
                        <div className="flex gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('icon', { ns: 'reminders' })}
                                </label>
                                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                                    {ICON_OPTIONS.map(ic => (
                                        <button
                                            key={ic}
                                            type="button"
                                            onClick={() => handleChange('icon', ic)}
                                            className={`w-10 h-10 text-xl rounded-lg flex items-center justify-center transition-all ${
                                                formData.icon === ic
                                                    ? 'bg-blue-100 ring-2 ring-blue-500'
                                                    : 'bg-white hover:bg-gray-100'
                                            }`}
                                        >
                                            {ic}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('color', { ns: 'reminders' })}
                                </label>
                                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                                    {COLOR_OPTIONS.map(c => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => handleChange('color', c)}
                                            className={`w-8 h-8 rounded-full transition-transform ${
                                                formData.color === c ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : ''
                                            }`}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('titleLabel', { ns: 'reminders' })} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('titlePlaceholder', { ns: 'reminders' })}
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('description', { ns: 'reminders' })}
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                rows={4}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('descriptionPlaceholder', { ns: 'reminders' })}
                            />
                        </div>

                        {/* Category & Priority */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('category', { ns: 'reminders' })}
                                </label>
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={t('categoryPlaceholder', { ns: 'reminders' })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('priorityLabel', { ns: 'reminders' })}
                                </label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => handleChange('priority', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {PRIORITY_OPTIONS.map(p => (
                                        <option key={p} value={p}>
                                            {t(`priority.${p}`, { ns: 'reminders' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <IconTag className="w-4 h-4 inline mr-1" />
                                {t('tags', { ns: 'reminders' })}
                            </label>
                            <div className="flex flex-wrap gap-2 mb-2">
                                {formData.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm"
                                    >
                                        #{tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="hover:text-red-600"
                                        >
                                            <IconX className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                    className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={t('addTag', { ns: 'reminders' })}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddTag}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                                >
                                    <IconPlus className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Schedule Tab */}
                {activeTab === 'schedule' && (
                    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                        {/* Start Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <IconCalendar className="w-4 h-4 inline mr-1" />
                                    {t('startDate', { ns: 'reminders' })} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.schedule.startDate}
                                    onChange={(e) => handleScheduleChange('startDate', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <IconCalendar className="w-4 h-4 inline mr-1" />
                                    {t('endDate', { ns: 'reminders' })}
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.schedule.endDate}
                                    onChange={(e) => handleScheduleChange('endDate', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        {/* Reminder Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <IconClock className="w-4 h-4 inline mr-1" />
                                    {t('reminderTime', { ns: 'reminders' })}
                                </label>
                                <input
                                    type="time"
                                    value={formData.schedule.time}
                                    onChange={(e) => handleScheduleChange('time', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('timezone', { ns: 'reminders' })}
                                </label>
                                <select
                                    value={formData.schedule.timezone}
                                    onChange={(e) => handleScheduleChange('timezone', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="Asia/Taipei">Asia/Taipei (UTC+8)</option>
                                    <option value="Asia/Shanghai">Asia/Shanghai (UTC+8)</option>
                                    <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
                                    <option value="America/New_York">America/New_York (UTC-5)</option>
                                    <option value="America/Los_Angeles">America/Los_Angeles (UTC-8)</option>
                                    <option value="Europe/London">Europe/London (UTC+0)</option>
                                </select>
                            </div>
                        </div>

                        {/* Recurrence Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <IconRepeat className="w-4 h-4 inline mr-1" />
                                {t('recurrenceType', { ns: 'reminders' })}
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {RECURRENCE_TYPES.map(type => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => handleRecurrenceChange('type', type)}
                                        className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                                            formData.schedule.recurrence.type === type
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {t(`recurrence.${type}`, { ns: 'reminders' })}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Recurrence Options */}
                        {formData.schedule.recurrence.type !== 'once' && (
                            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                                {/* Interval */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('interval', { ns: 'reminders' })}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span>{t('every', { ns: 'reminders' })}</span>
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.schedule.recurrence.interval}
                                            onChange={(e) => handleRecurrenceChange('interval', parseInt(e.target.value) || 1)}
                                            className="w-20 border border-gray-300 rounded-md px-3 py-1 text-center"
                                        />
                                        <span>
                                            {t(`recurrence.${formData.schedule.recurrence.type}Unit`, { ns: 'reminders' })}
                                        </span>
                                    </div>
                                </div>

                                {/* Days of Week for weekly */}
                                {formData.schedule.recurrence.type === 'weekly' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('daysOfWeek', { ns: 'reminders' })}
                                        </label>
                                        <div className="flex gap-2">
                                            {DAYS_OF_WEEK.map(day => (
                                                <button
                                                    key={day.value}
                                                    type="button"
                                                    onClick={() => handleDayOfWeekToggle(day.value)}
                                                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                                        (formData.schedule.recurrence.daysOfWeek || []).includes(day.value)
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {day.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Days of Month for monthly/quarterly/yearly - 多選 */}
                                {(formData.schedule.recurrence.type === 'monthly' ||
                                  formData.schedule.recurrence.type === 'quarterly' ||
                                  formData.schedule.recurrence.type === 'yearly') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('daysOfMonth', { ns: 'reminders' })} (可多選，如1號和15號)
                                        </label>
                                        <div className="grid grid-cols-7 gap-2">
                                            {[...Array(31)].map((_, i) => {
                                                const day = i + 1;
                                                return (
                                                    <button
                                                        key={day}
                                                        type="button"
                                                        onClick={() => handleDayOfMonthToggle(day)}
                                                        className={`px-3 py-2 text-sm rounded-md transition-colors ${
                                                            (formData.schedule.recurrence.daysOfMonth || []).includes(day)
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {day}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Days of Week for once/daily - 工作日篩選 */}
                        {(formData.schedule.recurrence.type === 'once' || 
                          formData.schedule.recurrence.type === 'daily') && (
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('daysOfWeekFilter', { ns: 'reminders' }) || '派送日篩選'} (可選)
                                </label>
                                <div className="flex gap-2">
                                    {DAYS_OF_WEEK.map(day => (
                                        <button
                                            key={day.value}
                                            type="button"
                                            onClick={() => handleDayOfWeekToggle(day.value)}
                                            className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                                (formData.schedule.recurrence.daysOfWeek || []).includes(day.value)
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    留空表示不篩選，每天都寄送
                                </p>
                            </div>
                        )}

                        {/* Schedule Preview - 排程預覽 */}
                        {futureOccurrences.length > 0 && (
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-blue-800">
                                        📅 {t('schedulePreview', { ns: 'reminders' }) || '排程預覽'}
                                    </label>
                                    <select
                                        value={previewCount}
                                        onChange={(e) => setPreviewCount(parseInt(e.target.value))}
                                        className="border border-blue-300 rounded-md px-2 py-1 text-sm bg-white"
                                    >
                                        <option value={5}>顯示 5 次</option>
                                        <option value={10}>顯示 10 次</option>
                                        <option value={15}>顯示 15 次</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    {futureOccurrences.map((date, idx) => (
                                        <div key={idx} className="text-sm text-blue-700 flex items-center gap-2">
                                            <span className="w-5 h-5 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium">
                                                {idx + 1}
                                            </span>
                                            <span>{formatOccurrence(date, formData.schedule.timezone)}</span>
                                        </div>
                                    ))}
                                </div>
                                {formData.schedule.recurrence.type !== 'once' && (
                                    <p className="text-xs text-blue-600 mt-2">
                                        {t('schedulePreviewHint', { ns: 'reminders' }) || `顯示接下來 ${previewCount} 次寄送時間`}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Collaborators Tab */}
                {activeTab === 'collaborators' && (
                    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    <IconUsers className="w-5 h-5 inline mr-2" />
                                    {t('collaborators', { ns: 'reminders' })}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {t('collaboratorsDesc', { ns: 'reminders' })}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowUserPicker(true)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <IconPlus className="w-4 h-4 mr-1" />
                                {t('addCollaborator', { ns: 'reminders' })}
                            </button>
                        </div>

                        {formData.collaborators.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <IconUsers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>{t('noCollaborators', { ns: 'reminders' })}</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {formData.collaborators.map(collab => (
                                    <div key={collab.userId} className="flex items-center justify-between py-3">
                                        <div>
                                            <p className="font-medium text-gray-900">{collab.userName}</p>
                                            <p className="text-sm text-gray-500">{collab.userEmail}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <select
                                                value={collab.permission}
                                                onChange={(e) => handleUpdateCollaboratorPermission(collab.userId, e.target.value)}
                                                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                                            >
                                                <option value="read">{t('permission.read', { ns: 'reminders' })}</option>
                                                <option value="write">{t('permission.write', { ns: 'reminders' })}</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCollaborator(collab.userId)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded"
                                            >
                                                <IconTrash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Notification Settings for Collaborators */}
                        <div className="pt-4 border-t border-gray-200">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.notifications.notifyCollaborators}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        notifications: { ...prev.notifications, notifyCollaborators: e.target.checked }
                                    }))}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                    {t('notifyCollaborators', { ns: 'reminders' })}
                                </span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Advanced Tab */}
                {activeTab === 'advanced' && (
                    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                        {/* Status (for edit mode) */}
                        {isEdit && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('statusLabel', { ns: 'reminders' })}
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => handleChange('status', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="active">{t('status.active', { ns: 'reminders' })}</option>
                                    <option value="paused">{t('status.paused', { ns: 'reminders' })}</option>
                                    <option value="completed">{t('status.completed', { ns: 'reminders' })}</option>
                                    <option value="archived">{t('status.archived', { ns: 'reminders' })}</option>
                                </select>
                            </div>
                        )}

                        {/* Notification Channels */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('notificationChannels', { ns: 'reminders' })}
                            </label>
                            <div className="space-y-2">
                                {['email', 'teams'].map(channel => (
                                    <label key={channel} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.notifications.channels.includes(channel)}
                                            onChange={(e) => {
                                                const channels = e.target.checked
                                                    ? [...formData.notifications.channels, channel]
                                                    : formData.notifications.channels.filter(c => c !== channel);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    notifications: { ...prev.notifications, channels }
                                                }));
                                            }}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">
                                            {t(`channel.${channel}`, { ns: 'reminders' })}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* External Source Integration Placeholder */}
                        {/* <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                                {t('externalIntegration', { ns: 'reminders' })}
                            </h4>
                            <p className="text-sm text-gray-500">
                                {t('externalIntegrationDesc', { ns: 'reminders' })}
                            </p>
                        </div> */}
                    </div>
                )}

                {/* Submit Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <Link
                        to="/reminders"
                        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        {t('cancel', { ns: 'common' })}
                    </Link>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                        {saving ? (
                            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                        ) : (
                            <IconSave className="w-4 h-4 mr-2" />
                        )}
                        {isEdit ? t('save', { ns: 'common' }) : t('create', { ns: 'reminders' })}
                    </button>
                </div>
            </form>

            {/* User Picker Modal */}
            {showUserPicker && (
                <UserMultiPicker
                    onSelect={handleAddCollaborators}
                    onClose={() => setShowUserPicker(false)}
                    excludeIds={[user?.id, ...formData.collaborators.map(c => c.userId)].filter(Boolean)}
                />
            )}
        </div>
    );
};

export default ReminderEditor;
